import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaUsers } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa6';
import {
  MdNewReleases, MdOnlinePrediction, MdListAlt, MdDashboard, MdArrowBack
} from 'react-icons/md';
import CrmListPage from 'components/app/crm/shared/CrmListPage';
import { columnDefs as baseColumns } from 'components/app/authorization/user/component/Columns';
import {
  getAllUsers,
  getOnlineUsers,
  getCreatedThisMonthUsers,
  exportUsers
} from 'redux/authorization/userSlice';
import { getAllSites } from 'redux/organization/siteSlice';
import UserDashboard from 'components/app/authorization/user/list/UserDashboard';
import UserFormModal from 'components/app/authorization/user/UserFormModal';

const columns = baseColumns.map((c) => ({ ...c, sortable: false }));

const TABS = [
  { key: 'all',                label: 'All',                Icon: MdListAlt,          thunk: getAllUsers,              dataKey: 'allData' },
  { key: 'created-this-month', label: 'Created this month', Icon: MdNewReleases,      thunk: getCreatedThisMonthUsers, dataKey: 'createdThisMonthData' },
  { key: 'online',             label: 'Online',             Icon: MdOnlinePrediction, thunk: getOnlineUsers,           dataKey: 'onlineData' },
];

const DATE_FILTERS = [
  { key: 'created_at', label: 'Created at', paramKeys: ['created_at_gteq', 'created_at_lteq'] },
];

const UserPage = () => {
  const dispatch = useDispatch();

  const { data: siteData = [] } = useSelector((s) => s.organization_site.allData);

  const [showDashboard, setShowDashboard] = useState(false);
  const [showModal, setShowModal]         = useState(false);
  const [reloadKey, setReloadKey]         = useState(0); // remount the list to refetch after create

  useEffect(() => {
    dispatch(getAllSites());
  }, [dispatch]);

  // Inline quick filters surfaced directly in the search bar.
  const selects = useMemo(() => [
    {
      key: 'status',
      paramKey: 'active_eq',
      options: [
        { value: '', label: 'All status' },
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Deactivated' }
      ]
    }
  ], []);

  // Site as a multi-select checkbox dropdown (like Sales unit).
  const multiSelects = useMemo(() => [
    {
      key: 'site',
      paramKey: 'site_id_in',
      placeholder: 'All sites',
      options: siteData.map((s) => ({ value: s.id, label: s.name }))
    }
  ], [siteData]);

  const headerExtra = (
    <>
      <button type="button" onClick={() => setShowDashboard(true)}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer">
        <MdDashboard size={15} /> Dashboard
      </button>
      <button type="button" onClick={() => setShowModal(true)}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-primary-600 tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-text-sm tw-font-medium tw-cursor-pointer">
        <FaPlus size={11} /> New User
      </button>
    </>
  );

  if (showDashboard) {
    return (
      <div className="tw-p-4">
        <button type="button" onClick={() => setShowDashboard(false)}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-mb-3 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer">
          <MdArrowBack size={15} /> Back to users
        </button>
        <UserDashboard />
      </div>
    );
  }

  return (
    <>
      <CrmListPage
        key={reloadKey}
        title="Users"
        subtitle="Manage system users and permissions."
        Icon={FaUsers}
        reducerSelector={(s) => s.authorization_user}
        tabs={TABS}
        columns={columns}
        searchParamKey="id_or_name_or_full_name_or_email_cont"
        searchPlaceholder="Search by name or email..."
        dateFilters={DATE_FILTERS}
        selects={selects}
        multiSelects={multiSelects}
        showSalesUnit
        salesUnitParamKey="sales_unit_id_in"
        exportThunk={exportUsers}
        headerExtra={headerExtra}
      />

      {showModal && (
        <UserFormModal
          show={showModal}
          user={null}
          onHide={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); setReloadKey((k) => k + 1); }}
        />
      )}
    </>
  );
};

export default UserPage;
